import { Button, Stack, useTheme } from "@mui/material";
import { Bell } from "lucide-react";
import { useState } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { reminderRowAnchorId } from "web_myplann/NotificationCenter/Utils/reminderScrollParam";
import { DeleteReminderDialog } from "web_myplann/Reminders/Components/DeleteReminderDialog";
import { ReminderActionsDrawer } from "web_myplann/Reminders/Components/ReminderActionsDrawer";
import { ReminderCreateDrawer } from "web_myplann/Reminders/Components/ReminderCreateDrawer";
import { ReminderEditDrawer } from "web_myplann/Reminders/Components/ReminderEditDrawer";
import { ReminderEmptyState } from "web_myplann/Reminders/Components/ReminderEmptyState";
import { ReminderListRow } from "web_myplann/Reminders/Components/ReminderListRow";
import { ReminderListRowSkeleton } from "web_myplann/Reminders/Components/ReminderListRowSkeleton";
import { ReminderSnoozeDrawer } from "web_myplann/Reminders/Components/ReminderSnoozeDrawer";
import { useScrollToReminderRow } from "web_myplann/Reminders/Hooks/useScrollToReminderRow";
import type {
	Reminder,
	ReminderEntity,
} from "web_myplann/Reminders/Types/ReminderTypes";
import { DataCard } from "web_myplann/Shared/Components/DataCard";

type ActiveDrawer =
	| { type: "none" }
	| { type: "create" }
	| { type: "edit"; reminder: Reminder }
	| { type: "actions"; reminder: Reminder }
	| { type: "snooze"; reminder: Reminder }
	| { type: "delete"; reminder: Reminder };

type Props = {
	entity: ReminderEntity;
	reminders: Reminder[] | undefined;
	isLoading: boolean;
	isError: boolean;
};

export const RemindersSectionView = ({
	entity,
	reminders = [],
	isLoading,
	isError,
}: Props) => {
	const { t } = useResource();
	const theme = useTheme();

	const [active, setActive] = useState<ActiveDrawer>({ type: "none" });

	useScrollToReminderRow(!isLoading && !isError);

	const close = () => setActive({ type: "none" });

	if (isError) {
		return null;
	}

	if (isLoading) {
		return (
			<DataCard
				label={t(Resources.Reminders.Section.title)}
				icon={<Bell size={16} color={theme.palette.purple.main} />}
				noDivider
			>
				<Stack gap={1.6}>
					<ReminderListRowSkeleton />
					<ReminderListRowSkeleton />
				</Stack>
			</DataCard>
		);
	}

	return (
		<>
			<DataCard
				label={t(Resources.Reminders.Section.title)}
				icon={<Bell size={16} color={theme.palette.purple.main} />}
				noDivider
			>
				{reminders.length === 0 ? (
					<ReminderEmptyState onAdd={() => setActive({ type: "create" })} />
				) : (
					<Stack gap={1.6}>
						{reminders.map((reminder) => (
							<ReminderListRow
								key={reminder.id}
								id={reminderRowAnchorId(reminder.id)}
								reminder={reminder}
								onTap={() => setActive({ type: "actions", reminder })}
								onRemove={() => setActive({ type: "delete", reminder })}
							/>
						))}
						<Button onClick={() => setActive({ type: "create" })}>
							{t(Resources.Reminders.Section.addButton)}
						</Button>
					</Stack>
				)}
			</DataCard>

			{active.type === "create" && (
				<ReminderCreateDrawer entity={entity} open onClose={close} />
			)}

			{active.type === "edit" && (
				<ReminderEditDrawer
					entity={entity}
					reminder={active.reminder}
					open
					onClose={close}
				/>
			)}

			{active.type === "actions" && (
				<ReminderActionsDrawer
					reminder={active.reminder}
					open
					onClose={close}
					onEdit={() => setActive({ type: "edit", reminder: active.reminder })}
					onSnooze={() =>
						setActive({ type: "snooze", reminder: active.reminder })
					}
					onDelete={() =>
						setActive({ type: "delete", reminder: active.reminder })
					}
				/>
			)}

			{active.type === "snooze" && (
				<ReminderSnoozeDrawer reminder={active.reminder} open onClose={close} />
			)}

			{active.type === "delete" && (
				<DeleteReminderDialog reminder={active.reminder} open onClose={close} />
			)}
		</>
	);
};
